/**
 * SQLite JSON-path extraction from cursor context.
 *
 * Given the buffer text and a cursor offset inside a comparison RHS string
 * literal (`WHERE metadata->>'$.status' = '|'`), detects whether the LHS of
 * the comparison is a SQLite JSON-extract expression and returns the
 * normalized canonical JSONPath (`$.status`) so the enum-value resolver can
 * look up path-keyed hints.
 *
 * Supported SQLite JSON path forms (v1):
 *   col->>'$.key'                       → canonical `$.key`
 *   col->>'$.a.b'                       → canonical `$.a.b`
 *   col->>'$.items[0].name'             → canonical `$.items[0].name`
 *   json_extract(col, '$.key')          → canonical `$.key`
 *   json_extract(col, '$.a.b')          → canonical `$.a.b`
 *   json_extract(col, '$.items[0].name')→ canonical `$.items[0].name`
 *   col->>'key'                         → canonical `$.key`  (bare-key parity)
 *
 * SQLite's `->>` JSON-extract operator (3.38+) accepts a path argument that
 * always starts with `$.` in canonical form, but the implementation also
 * accepts bare keys for parity with the cross-engine MetaInfo path-keying.
 *
 * Pure text scanner — no tree-sitter dependency. Mirrors the backward-scan
 * pattern of `extractJsonPathFromCursor.pgsql.ts` and `comparisonScanHelpers`.
 */

import { normalizeJsonPath } from '@/features/workspace/common/utils/jsonFilterSql';
import {
  skipValueOperand,
  COMPARISON_OPERATORS,
  endsWithAt,
} from '../context/comparisonScanHelpers';

export type ExtractJsonPathResult = {
  /** Already passed through normalizeJsonPath. */
  readonly canonicalPath: string;
  /** The column name (unqualified, case-preserved) from the LHS. */
  readonly column: string;
};

/**
 * Returns the normalized JSON path when the cursor sits at the comparison
 * RHS of a SQLite JSON-extract expression, or `null` otherwise.
 *
 * `column` is the column name from the resolved table column binding — used
 * to verify the extract expression targets the same column so we don't offer
 * completions for the wrong column.
 */
export function extractJsonPathFromCursorSqlite(args: {
  readonly text: string;
  readonly cursorOffset: number;
  /** Omit to accept whichever column the extract expression names — the
   *  scan itself never depends on it (the JSON-chain enum fallback scans
   *  once, then matches the returned `column` against the snapshot). */
  readonly column?: string;
}): ExtractJsonPathResult | null {
  const { text, cursorOffset, column } = args;
  if (cursorOffset < 0 || cursorOffset > text.length) return null;

  // Walk backward from the cursor past the in-progress string literal to land
  // on the comparison operator boundary (exclusive end of LHS).
  const opBoundary = findComparisonOpEndBefore(text, cursorOffset);
  if (opBoundary === null) return null;

  // The token immediately before the comparison operator should be the
  // closing `'` of a `->>` path literal OR the closing `)` of a
  // `json_extract(col, 'path')` call. Skip whitespace.
  let i = opBoundary - 1;
  while (i >= 0 && /\s/.test(text[i]!)) i--;
  if (i < 0) return null;

  // Try the `json_extract(col, 'path')` form first — its closing `)` is
  // the trailing char of the LHS.
  if (text[i] === ')') {
    const extracted = parseJsonExtractCallEndingAt(text, i);
    if (!extracted) return null;
    if (column !== undefined && extracted.column.toLowerCase() !== column.toLowerCase()) return null;
    return extracted;
  }

  // Otherwise expect the closing `'` of a `->>` path literal.
  if (text[i] !== "'") return null;

  const extracted = parseDashChainExprAt(text, i);
  if (!extracted) return null;

  if (column !== undefined && extracted.column.toLowerCase() !== column.toLowerCase()) return null;
  return extracted;
}

// ─────────────────────────────────────────────────────────────────────────────
// Comparison operator boundary scan
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Walk back from `offset` over the optional in-progress RHS string literal,
 * using `skipValueOperand` and return the exclusive-end index of the LHS
 * expression (i.e., `opBoundary` where `text.slice(0, opBoundary)` ends with
 * the comparison operator). Returns `null` when no recognized comparison
 * operator precedes the cursor's operand position.
 */
function findComparisonOpEndBefore(text: string, offset: number): number | null {
  const i = skipValueOperand(text, offset - 1);
  if (i < 0) return null;

  const opEnd = i + 1;
  for (const op of COMPARISON_OPERATORS) {
    if (endsWithAt(text, opEnd, op)) {
      return opEnd - op.length;
    }
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Dash-chain extract `col->>'path'`
// ─────────────────────────────────────────────────────────────────────────────

type ExtractResult = { column: string; canonicalPath: string };

/**
 * Parse a SQLite `col->>'path'` expression whose closing `'` sits at `end`.
 * Returns `null` when the pattern isn't recognized.
 */
function parseDashChainExprAt(text: string, end: number): ExtractResult | null {
  // Read the path literal at `end`.
  const lit = readQuotedStringEndingAt(text, end);
  if (!lit) return null;

  // Identify the operator immediately before the opening quote.
  let j = lit.openIdx - 1;
  while (j >= 0 && /\s/.test(text[j]!)) j--;
  if (j < 0) return null;

  const threeBack = text.slice(Math.max(0, j - 2), j + 1);

  if (threeBack.endsWith('->>')) {
    // col->>'path' — op spans j-2..j, so char before op is at j-3.
    const beforeOp = j - 3;
    const col = readBareIdentBefore(text, beforeOp);
    if (!col) return null;
    return {
      column: col,
      canonicalPath: normalizeJsonPath(lit.content.trim()),
    };
  }

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// `json_extract(col, 'path')` call form
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parse a `json_extract(col, 'path')` call whose closing `)` sits at `end`.
 * Returns `null` when the pattern isn't recognized.
 *
 * Tolerates surrounding whitespace inside the arg list:
 *   json_extract( col , '$.a.b' )
 */
function parseJsonExtractCallEndingAt(
  text: string,
  end: number,
): ExtractResult | null {
  if (text[end] !== ')') return null;

  // Walk back past the closing `)` to the path argument literal.
  let i = end - 1;
  while (i >= 0 && /\s/.test(text[i]!)) i--;
  if (i < 0 || text[i] !== "'") return null;

  const lit = readQuotedStringEndingAt(text, i);
  if (!lit) return null;

  // Expect `,` before the literal.
  let k = lit.openIdx - 1;
  while (k >= 0 && /\s/.test(text[k]!)) k--;
  if (k < 0 || text[k] !== ',') return null;

  // Read the column-name argument. The argument may be a bare or dotted
  // identifier (`metadata`, `t.metadata`, `"t"."metadata"`).
  k -= 1;
  while (k >= 0 && /\s/.test(text[k]!)) k--;
  if (k < 0) return null;
  const col = readBareIdentBefore(text, k);
  if (!col) return null;

  // Find the start of the (possibly dotted) column identifier so we can
  // verify the `(` and function name that precede it.
  let identStart = k + 1;
  while (identStart > 0 && /[A-Za-z0-9_"]/.test(text[identStart - 1]!)) identStart--;
  // Optionally consume `.<qualifier>` segments (alias / schema / database
  // qualifiers): `t.metadata`, `"t"."metadata"`, `main.t.metadata`.
  for (;;) {
    let p = identStart - 1;
    while (p >= 0 && /\s/.test(text[p]!)) p--;
    if (p < 0 || text[p] !== '.') break;
    p -= 1;
    while (p >= 0 && /\s/.test(text[p]!)) p--;
    const segEnd = p + 1;
    while (p >= 0 && /[A-Za-z0-9_"]/.test(text[p]!)) p--;
    if (p + 1 === segEnd) break; // no identifier segment before the dot
    identStart = p + 1;
  }

  let m = identStart - 1;
  while (m >= 0 && /\s/.test(text[m]!)) m--;
  if (m < 0 || text[m] !== '(') return null;

  m -= 1;
  while (m >= 0 && /\s/.test(text[m]!)) m--;
  // Read the function name immediately before `(`.
  const fnEnd = m + 1;
  while (m >= 0 && /[A-Za-z0-9_]/.test(text[m]!)) m--;
  const fn = text.slice(m + 1, fnEnd);
  if (fn.toLowerCase() !== 'json_extract') return null;

  return {
    column: col,
    canonicalPath: normalizeJsonPath(lit.content.trim()),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// String literal + identifier helpers
// ─────────────────────────────────────────────────────────────────────────────

type QuotedString = {
  content: string;
  /** Index of the opening `'`. */
  openIdx: number;
};

function readQuotedStringEndingAt(text: string, end: number): QuotedString | null {
  if (text[end] !== "'") return null;
  let i = end - 1;
  let content = '';
  while (i >= 0) {
    const ch = text[i]!;
    if (ch === "'") {
      if (i > 0 && text[i - 1] === "'") {
        content = "'" + content;
        i -= 2;
        continue;
      }
      return { content, openIdx: i };
    }
    if (ch === '\n') return null;
    content = ch + content;
    i--;
  }
  return null;
}

function readBareIdentBefore(text: string, end: number): string | null {
  let j = end;
  while (j >= 0 && /\s/.test(text[j]!)) j--;
  if (j < 0) return null;
  const idEnd = j + 1;
  // Accept double-quoted identifiers ("metadata") in addition to bare ones.
  // SQLite also accepts `[name]` and `` `name` `` but for the LHS column-ref
  // of a JSON-extract expression we follow the same minimal set as the PG
  // extractor — round-trip through the resolver's identifier helpers.
  while (j >= 0 && /[A-Za-z0-9_"]/.test(text[j]!)) j--;
  if (j + 1 === idEnd) return null;
  const id = text.slice(j + 1, idEnd).replace(/^"|"$/g, '');
  if (id.length === 0 || /^\d/.test(id)) return null;
  return id;
}
